import { Button } from "../../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../../components/ui/card";
import { Checkbox } from "../../../../../../components/ui/checkbox";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import { MultiSelect } from "../../../../../../components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../components/ui/select";
import { QrCode } from "lucide-react";
import { SpecificationDocument } from "../../../../types";
import { Materials } from "../../../../types/materials";
import { useTeamMembers } from "../../../../hooks/useTeamMembers";
import { Date as DateInput } from "../../../../../../components/ui/date";

interface ProductionTabProps {
  specifications: SpecificationDocument[];
  materials: Materials[];
  selectedPipeSize: number;
  selectedGasIds: string[];
  setSelectedGasIds: (ids: string[]) => void;
  selectedWireIds: string[];
  setSelectedWireIds: (ids: string[]) => void;
  selectedForgeId: string | null;
  handleForgeChange: (value: string) => void;
  selectedPipeId: string | null;
  handlePipeChange: (value: string) => void;
  distanceFromPipeEnd: number | null;
  handleDistanceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleQRScan: (type: 'forge' | 'pipe') => void;
  customerSignature: string;
  setCustomerSignature: (signature: string) => void;
  qcSignature: string;
  setQcSignature: (signature: string) => void;
}

export const ProductionTab: React.FC<ProductionTabProps> = ({
  specifications,
  materials,
  selectedPipeSize,
  selectedGasIds,
  setSelectedGasIds,
  selectedWireIds,
  setSelectedWireIds,
  selectedForgeId,
  handleForgeChange,
  selectedPipeId,
  handlePipeChange,
  distanceFromPipeEnd,
  handleDistanceChange,
  handleQRScan,
  customerSignature,
  setCustomerSignature,
  qcSignature,
  setQcSignature,
}) => {
  const { teamMembers } = useTeamMembers();

  // Create options for gas and wire lot dropdowns
  const gasOptions = materials
    .filter(m => 
      m.category === 'Gas' && 
      m.pipeId === `pipe-${selectedPipeSize}` &&
      m.status === 'Available'
    )
    .map(gas => ({
      value: gas.id,
      label: gas.gasSerialNumber || ''
    }));

  const wireLotOptions = materials
    .filter(m => 
      m.category === 'Wire Lot' && 
      m.pipeId === `pipe-${selectedPipeSize}` &&
      m.status === 'Available'
    )
    .map(wire => ({
      value: wire.id,
      label: wire.lotNumber || ''
    }));

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        {/* Production Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Production Details</h3>
          <div className="grid grid-cols-4 gap-4">
            {/* Row 1: Documentation and Basic Info */}
            <div className="space-y-2">
              <Label>WPS Number</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select WPS" />
                </SelectTrigger>
                <SelectContent>
                  {specifications
                    .filter(spec => spec.type === 'WPS' && spec.pipeId === `pipe-${selectedPipeSize}`)
                    .map(wps => (
                      <SelectItem key={wps.id} value={wps.id}>
                        {wps.fileName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Drawing Number</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select drawing" />
                </SelectTrigger>
                <SelectContent>
                  {specifications
                    .filter(spec => spec.type === 'drawing' && spec.pipeId === `pipe-${selectedPipeSize}`)
                    .map(drawing => (
                      <SelectItem key={drawing.id} value={drawing.id}>
                        {drawing.fileName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Gas Serial Numbers</Label>
              <MultiSelect
                options={gasOptions}
                selected={selectedGasIds}
                onChange={setSelectedGasIds}
                placeholder="Select gas serial numbers..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label>Wire Lot Numbers</Label>
              <MultiSelect
                options={wireLotOptions}
                selected={selectedWireIds}
                onChange={setSelectedWireIds}
                placeholder="Select wire lot numbers..."
                className="w-full"
              />
            </div>

            {/* Row 2: Physical Components and Measurements */}
            <div className="space-y-2">
              <Label>Forge Number</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={selectedForgeId || ''} onValueChange={handleForgeChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select forge" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials
                        .filter(m => m.category === 'Forge' && m.pipeId === `pipe-${selectedPipeSize}` && m.status === 'Available')
                        .map(forge => (
                          <SelectItem key={forge.id} value={forge.id}>
                            {forge.forgeSerialNumber}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="shrink-0"
                  onClick={() => handleQRScan('forge')}
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Pipe Number</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={selectedPipeId || ''} onValueChange={handlePipeChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pipe" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials
                        .filter(m => m.category === 'Pipe' && m.pipeId === `pipe-${selectedPipeSize}` && m.status === 'Available')
                        .map(pipe => (
                          <SelectItem key={pipe.id} value={pipe.id}>
                            {pipe.pipeNo}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="shrink-0"
                  onClick={() => handleQRScan('pipe')}
                >
                  <QrCode className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Distance from Pipe End to Forge (mm)</Label>
              <Input 
                type="number" 
                placeholder="Enter distance in mm"
                min="0"
                step="1"
                value={distanceFromPipeEnd || ''}
                onChange={handleDistanceChange}
              />
            </div>

            <div className="space-y-2">
              <Label>Production Date</Label>
              <DateInput 
                type="date" 
                defaultValue={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>

        {/* Assembly Checks */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Assembly Checks</h3>
          <div className="flex items-center gap-8">
            <label className="flex items-center gap-2">
              <Checkbox />
              <span className="text-sm font-medium">Fitment</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox />
              <span className="text-sm font-medium">Visual Inspection</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox />
              <span className="text-sm font-medium">NDT</span>
            </label>
          </div>
        </div>

        {/* Weld and Inspection Traceability */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium">Weld and Inspection Traceability</h3>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Weld 1 */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-medium">Weld 1</h4>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#e04530] font-medium">WLD-2024-001</span>
                  <QrCode className="h-5 w-5 text-gray-600" />
                </div>
              </div>

              {/* Welder Selection */}
              <div className="w-full">
                <Label>Welder</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select welder" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Placeholder welders - to be populated from team members */}
                    <SelectItem value="welder1">John Doe (Certified Welder)</SelectItem>
                    <SelectItem value="welder2">Jane Smith (Certified Welder)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Passes */}
              <div className="space-y-3">
                <div className="grid grid-cols-12 items-center border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium col-span-3">Passes</span>
                  <span className="text-sm font-medium col-span-2 text-center">Status</span>
                  <div className="col-span-7 text-center">
                    <span className="text-sm font-medium">Pre heat or interpass temperature (°C)</span>
                  </div>
                </div>
                {['Root Pass', 'Hot Pass', 'Fill Pass', 'Cap Pass'].map((pass, index) => (
                  <div key={index} className="grid grid-cols-12 items-center border-b border-gray-200 pb-3">
                    <span className="text-sm font-medium col-span-3">{pass}</span>
                    <div className="col-span-2 flex justify-center">
                      <Checkbox />
                    </div>
                    <div className="col-span-7 flex justify-center">
                      <Input 
                        type="number" 
                        placeholder="Temperature"
                        className="w-48"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weld 2 */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-medium">Weld 2</h4>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#e04530] font-medium">WLD-2024-002</span>
                  <QrCode className="h-5 w-5 text-gray-600" />
                </div>
              </div>

              {/* Welder Selection */}
              <div className="w-full">
                <Label>Welder</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select welder" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Placeholder welders - to be populated from team members */}
                    <SelectItem value="welder1">John Doe (Certified Welder)</SelectItem>
                    <SelectItem value="welder2">Jane Smith (Certified Welder)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Passes */}
              <div className="space-y-3">
                <div className="grid grid-cols-12 items-center border-b border-gray-200 pb-2">
                  <span className="text-sm font-medium col-span-3">Passes</span>
                  <span className="text-sm font-medium col-span-2 text-center">Status</span>
                  <div className="col-span-7 text-center">
                    <span className="text-sm font-medium">Pre heat or interpass temperature (°C)</span>
                  </div>
                </div>
                {['Root Pass', 'Hot Pass', 'Fill Pass', 'Cap Pass'].map((pass, index) => (
                  <div key={index} className="grid grid-cols-12 items-center border-b border-gray-200 pb-3">
                    <span className="text-sm font-medium col-span-3">{pass}</span>
                    <div className="col-span-2 flex justify-center">
                      <Checkbox />
                    </div>
                    <div className="col-span-7 flex justify-center">
                      <Input 
                        type="number" 
                        placeholder="Temperature"
                        className="w-48"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Signature Panel */}
        <div className="space-y-4 pt-6 border-t">
          <h3 className="text-lg font-medium">Signatures</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Customer Representative</Label>
              <Select
                value={customerSignature || undefined}
                onValueChange={setCustomerSignature}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer representative" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers
                    .filter(member => member.role === 'Customer Representative')
                    .map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {customerSignature && (
                <div className="mt-2 p-3 border rounded-md bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Signed by:</span>
                    <span className="text-sm font-medium">
                      {teamMembers.find(m => m.id === customerSignature)?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-500">Date:</span>
                    <span className="text-sm">{new Date().toISOString().split('T')[0]}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>QC Inspector</Label>
              <Select
                value={qcSignature || undefined}
                onValueChange={setQcSignature}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select QC inspector" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers
                    .filter(member => member.role === 'QC Inspector')
                    .map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {qcSignature && (
                <div className="mt-2 p-3 border rounded-md bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Signed by:</span>
                    <span className="text-sm font-medium">
                      {teamMembers.find(m => m.id === qcSignature)?.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-500">Date:</span>
                    <span className="text-sm">{new Date().toISOString().split('T')[0]}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 